/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import { SUPPORT_NO_ANSWER_ACTIONS_BLOCK_TYPES } from '../../../enums/AIBlocks';
import { normalizeScanStatus } from './attachmentScan';
import { ACTIVE_SUPPORT_MODES, SUPPORT_CONTACT_EMAIL_MAX_CHARS, SUPPORT_CONTACT_EMAIL_PATTERN, SUPPORT_CONTENT_KEY_AUTHOR, SUPPORT_CONTENT_KEY_HIDDEN, SUPPORT_CONTENT_KEY_ORIGIN, SUPPORT_CONTENT_KEY_UNAVAILABLE_ATTACHMENTS, SUPPORT_AVAILABILITY_REASON, SUPPORT_COPY, SUPPORT_DEFAULT_AUTHOR_NAME, SUPPORT_MESSAGE_ORIGIN, SUPPORT_NO_ACTION_BAR_ROLES, SUPPORT_NOTICE, SUPPORT_OFFER_STATE, SUPPORT_ROLE_HUMAN, SUPPORT_ROLE_SYSTEM } from '../constants/support';
// ---------------------------------------------------------------------------
// Classifying a message
//
// Persisted rows reach the widget through the history route, which returns ONLY
// `content` — `metadata` is not serialized. Live SSE messages are normalized into
// `metadata` by `supportPayloadToChatMessage`. Every predicate therefore reads
// BOTH places, so a message classifies the same way before and after a reload.
// ---------------------------------------------------------------------------
function contentRecord(message) {
    return typeof message.content === 'object' && message.content !== null
        ? message.content
        : {};
}
/** True for a message that belongs to the human support conversation (either direction). */
export function isSupportMessage(message) {
    var _a;
    return (message.role === SUPPORT_ROLE_HUMAN ||
        ((_a = message.metadata) === null || _a === void 0 ? void 0 : _a.origin) === SUPPORT_MESSAGE_ORIGIN ||
        contentRecord(message)[SUPPORT_CONTENT_KEY_ORIGIN] === SUPPORT_MESSAGE_ORIGIN);
}
/** Zendesk automation notices: stored for the record, never rendered, never counted unread. */
export function isHiddenSupportMessage(message) {
    var _a;
    if (message.role !== SUPPORT_ROLE_HUMAN && !isSupportMessage(message))
        return false;
    return ((_a = message.metadata) === null || _a === void 0 ? void 0 : _a.hidden) === true || contentRecord(message)[SUPPORT_CONTENT_KEY_HIDDEN] === true;
}
/** A customer message sent to a human agent (role `user`, support origin). */
export function isCustomerSupportMessage(message) {
    return message.role === 'user' && isSupportMessage(message);
}
/** The agent display name, falling back to the generic label when the provider gave none. */
export function supportAuthorName(message) {
    var _a, _b, _c;
    const fromMeta = (_b = (_a = message.metadata) === null || _a === void 0 ? void 0 : _a.author) === null || _b === void 0 ? void 0 : _b.name;
    const rawContentAuthor = contentRecord(message)[SUPPORT_CONTENT_KEY_AUTHOR];
    const fromContent = rawContentAuthor !== null && typeof rawContentAuthor === 'object'
        ? rawContentAuthor.name
        : undefined;
    const name = [fromMeta, fromContent].find((n) => typeof n === 'string' && n.trim() !== '');
    return (_c = name === null || name === void 0 ? void 0 : name.trim()) !== null && _c !== void 0 ? _c : SUPPORT_DEFAULT_AUTHOR_NAME;
}
/**
 * The agent's avatar URL as the server sent it, from either place the author lives (live events
 * in `metadata`, history in `content`). Unvalidated: the organism that renders it allows only
 * https images from gravatar.com / *.zendesk.com / *.zdusercontent.com.
 */
export function supportAuthorAvatarUrl(message) {
    var _a, _b;
    const rawContentAuthor = contentRecord(message)[SUPPORT_CONTENT_KEY_AUTHOR];
    const fromContent = rawContentAuthor !== null && typeof rawContentAuthor === 'object'
        ? rawContentAuthor.avatar_url
        : undefined;
    const candidate = [(_b = (_a = message.metadata) === null || _a === void 0 ? void 0 : _a.author) === null || _b === void 0 ? void 0 : _b.avatar_url, fromContent].find((u) => typeof u === 'string' && u.trim() !== '');
    return candidate !== null && candidate !== void 0 ? candidate : null;
}
/**
 * Which route serves a support message's files. The support attachments route only knows files on
 * a PERSISTED support message. The customer's own message is not persisted while it is still
 * sending (or after it failed): its files are only known to the widget's upload path
 * (`/api/files/{id}/refresh-url`), so they go straight there instead of through an expected 404.
 */
export function supportAttachmentSource(message) {
    var _a;
    const delivery = (_a = message.metadata) === null || _a === void 0 ? void 0 : _a.delivery;
    return message.role === 'user' && (delivery === 'sending' || delivery === 'failed') ? 'upload' : 'support';
}
/**
 * Files the agent sent that the server could not pass on (unsupported type, too large, fetch
 * failed), from either place they live: live events in `metadata`, history in `content`.
 */
export function supportUnavailableAttachments(message) {
    var _a, _b;
    const fromContent = contentRecord(message)[SUPPORT_CONTENT_KEY_UNAVAILABLE_ATTACHMENTS];
    const raw = (_b = (_a = message.metadata) === null || _a === void 0 ? void 0 : _a.unavailable_attachments) !== null && _b !== void 0 ? _b : (Array.isArray(fromContent) ? fromContent : []);
    return raw.flatMap((entry) => {
        if (entry === null || typeof entry !== 'object')
            return [];
        const { name, reason } = entry;
        if (typeof name !== 'string' || name.trim() === '')
            return [];
        return [{ name, reason: typeof reason === 'string' ? reason : undefined }];
    });
}
/**
 * A support message with nothing to show: no text, no files, and no notice about a dropped file.
 * It renders as nothing at all, never as an empty bubble under a header.
 */
export function isEmptySupportMessage(message) {
    var _a;
    if (message.role !== SUPPORT_ROLE_HUMAN)
        return false;
    const content = contentRecord(message);
    const text = typeof message.content === 'string' ? message.content : content.text;
    const files = (_a = message.file_attachments) !== null && _a !== void 0 ? _a : content.file_attachments;
    return ((typeof text !== 'string' || text.trim() === '') &&
        !(Array.isArray(files) && files.length > 0) &&
        supportUnavailableAttachments(message).length === 0);
}
/**
 * A replayed offer block that would render nothing: it is not expired (an expired one renders its
 * own muted line) and offers no channel to click.
 */
function isInertOfferBlock(block) {
    if (block.type !== 'support_handoff_offer')
        return false;
    const offer = block;
    if (offer.state === SUPPORT_OFFER_STATE.EXPIRED)
        return false;
    return !(offer.hours_open && offer.live_chat_available) && !offer.email_available;
}
/**
 * An assistant message whose whole content is offer cards with nothing to show (B46). History now
 * returns the card-only message, and a card that renders nothing would leave an empty bubble
 * under the agent badge, so the list drops the message instead. (An agent badge alone does not
 * count as content; any other block does.)
 */
export function isInertOfferMessage(message) {
    var _a, _b;
    const blocks = ((_b = (_a = message.envelope) === null || _a === void 0 ? void 0 : _a.blocks) !== null && _b !== void 0 ? _b : []).filter((block) => block.type !== 'agent_badge');
    return blocks.length > 0 && blocks.every(isInertOfferBlock);
}
/** Messages the list may render: hidden system notices are dropped here, at the single choke point. */
export function withoutHiddenSupportMessages(messages) {
    return messages.filter((message) => !isHiddenSupportMessage(message));
}
const SUPPORT_NOTICE_KINDS = Object.values(SUPPORT_NOTICE);
/**
 * Which support notice a history row is ("You emailed InspectPoint Support..."), or null for any
 * other row. The marker is `row.support_notice` on a `role: 'system'` row; both must hold, so a
 * stray field on an ordinary message can never turn it into a status line.
 */
export function supportNoticeKind(message) {
    if (message.role !== SUPPORT_ROLE_SYSTEM)
        return null;
    const kind = message.support_notice;
    return typeof kind === 'string' && SUPPORT_NOTICE_KINDS.includes(kind) ? kind : null;
}
/** The plain text of a notice row: the server's own sentence, or the fallback for its kind. */
export function supportNoticeText(message, kind) {
    const text = typeof message.content === 'string' ? message.content.trim() : '';
    return text || SUPPORT_COPY.NOTICE_FALLBACK[kind];
}
/**
 * The live `support_notice` event as the history row it mirrors (B41): same id (the persisted
 * row's), `role: 'system'`, the kind at the top level and the sentence as `content`. Because the
 * id IS the row's, appending it to the message cache is a no-op when history already holds it, and
 * a history load that comes later simply replaces it. Null for an event that is not a usable
 * notice (no id, or a kind this widget does not know), which is dropped rather than shown.
 *
 * A notice is a record, not a support message: it carries no origin marker, so it can never be
 * classified as one, counted unread, or move the catch-up cursor.
 */
export function supportNoticeToChatMessage(envelope) {
    var _a;
    const { payload } = envelope;
    const kind = payload === null || payload === void 0 ? void 0 : payload.support_notice;
    if (typeof (payload === null || payload === void 0 ? void 0 : payload.message_id) !== 'string' || payload.message_id === '')
        return null;
    if (typeof kind !== 'string' || !SUPPORT_NOTICE_KINDS.includes(kind))
        return null;
    const text = typeof payload.text === 'string' ? payload.text.trim() : '';
    return {
        id: payload.message_id,
        role: SUPPORT_ROLE_SYSTEM,
        content: text || SUPPORT_COPY.NOTICE_FALLBACK[kind],
        support_notice: kind,
        sequence: envelope.sequence,
        created_at: (_a = payload.created_at) !== null && _a !== void 0 ? _a : new Date().toISOString()
    };
}
// ---------------------------------------------------------------------------
// Ordering
// ---------------------------------------------------------------------------
function hasSequence(message) {
    return typeof message.sequence === 'number' && Number.isFinite(message.sequence);
}
/**
 * Order support rows by `ai_messages.sequence` wherever BOTH sides of the comparison carry one.
 * A live or catch-up event can land after a newer history row (or before an older one), and
 * arrival order is not conversation order; the sequence is.
 *
 * Only the rows that HAVE a sequence move, and only among the positions those rows already
 * occupy: an AI turn, a streaming copy or an optimistic send (no sequence) keeps its place, so
 * this can never shuffle the rest of the conversation. Stable for equal sequences. Returns the
 * same array when nothing needs to move.
 */
export function orderBySequence(messages) {
    const slots = messages.flatMap((message, index) => (hasSequence(message) ? [index] : []));
    if (slots.length < 2)
        return messages;
    const sorted = slots
        .map((index) => messages[index])
        .map((message, order) => ({ message, order }))
        .sort((a, b) => a.message.sequence - b.message.sequence || a.order - b.order)
        .map(({ message }) => message);
    if (sorted.every((message, i) => message === messages[slots[i]]))
        return messages;
    const result = messages.slice();
    slots.forEach((slot, i) => {
        result[slot] = sorted[i];
    });
    return result;
}
// ---------------------------------------------------------------------------
// Reply address typed into the offer card
// ---------------------------------------------------------------------------
/** Cheap client-side check so an obvious typo never costs a round trip; the server decides the rest. */
export function isValidContactEmail(raw) {
    const value = raw.trim();
    return (value.length > 0 && value.length <= SUPPORT_CONTACT_EMAIL_MAX_CHARS && SUPPORT_CONTACT_EMAIL_PATTERN.test(value));
}
/**
 * The open request's number as it may be quoted to the customer: nothing for a missing one or the
 * `0` a lower environment reports (no real request exists, so there is no number to show).
 */
export function displayableTicketNumber(raw) {
    const value = raw === null || raw === undefined ? '' : String(raw).trim();
    return value === '' || value === '0' ? null : value;
}
// ---------------------------------------------------------------------------
// SSE payload -> ChatMessage
// ---------------------------------------------------------------------------
export function supportAttachmentToFileRef(attachment) {
    var _a;
    return Object.assign({ file_id: attachment.file_id, file_name: attachment.name, mime_type: attachment.content_type, file_size_bytes: (_a = attachment.size) !== null && _a !== void 0 ? _a : 0, 
        // No verdict means the orchestrator already scanned it before relaying; a verdict that says
        // otherwise (still scanning, failed) is kept so the chip can say so.
        scan_status: normalizeScanStatus(attachment.scan_status) }, (attachment.is_image !== undefined ? { is_image: attachment.is_image } : {}));
}
/** Image verdict for a support attachment: the server's flag when sent, else the content type. */
export function isImageAttachment(ref) {
    var _a;
    return (_a = ref.is_image) !== null && _a !== void 0 ? _a : ref.mime_type.toLowerCase().startsWith('image/');
}
export function supportPayloadToChatMessage(envelope) {
    var _a, _b, _c, _d;
    const { payload } = envelope;
    const attachments = ((_a = payload.attachments) !== null && _a !== void 0 ? _a : []).map(supportAttachmentToFileRef);
    const unavailable = ((_b = payload.unavailable_attachments) !== null && _b !== void 0 ? _b : []).filter((entry) => typeof (entry === null || entry === void 0 ? void 0 : entry.name) === 'string');
    const metadata = Object.assign({ origin: (_c = payload.origin) !== null && _c !== void 0 ? _c : SUPPORT_MESSAGE_ORIGIN, author: payload.author }, (unavailable.length > 0 ? { unavailable_attachments: unavailable } : {}));
    return {
        id: payload.message_id,
        role: payload.role,
        content: payload.text,
        file_attachments: attachments.length > 0 ? attachments : undefined,
        metadata,
        sequence: envelope.sequence,
        created_at: (_d = payload.created_at) !== null && _d !== void 0 ? _d : new Date().toISOString()
    };
}
// ---------------------------------------------------------------------------
// Session helpers
// ---------------------------------------------------------------------------
/**
 * Why "Continue this chat" is off for a loaded past chat, or null when live chat is open. An
 * organization redirected to its own IT team is not "closed right now" (that is permanent), so it
 * gets its own message instead of the temporary one; everything else keeps the hours wording.
 */
export function pastChatUnavailableNote(state) {
    if (state.available.live_chat)
        return null;
    if (state.available.reason === SUPPORT_AVAILABILITY_REASON.ORG_RESTRICTED)
        return SUPPORT_COPY.PAST_CHAT_ORG_RESTRICTED;
    return SUPPORT_COPY.PAST_CHAT_UNAVAILABLE(state.hours.open ? undefined : state.hours.note);
}
export function isActiveSupportMode(mode) {
    return mode !== null && mode !== undefined && ACTIVE_SUPPORT_MODES.includes(mode);
}
/**
 * Whether `incoming` may replace `current`. The server's `mode_version` is the clock: a
 * different session id is adopted (the orchestrator only ever sends the newest), the same
 * session is adopted only at an equal-or-newer version so a delayed event can never roll the
 * view back (e.g. show "Waiting" again after "Chat ended").
 */
export function shouldAdoptSession(current, incoming) {
    if (!current || current.id !== incoming.id)
        return true;
    return incoming.mode_version >= current.mode_version;
}
/**
 * Whether a message must NOT carry the AI answer action bar (copy / thumbs up / thumbs down /
 * regenerate). Two cases, both keyed off the shared support sets:
 *  - a human support message: a person's words are not an answer to rate or regenerate;
 *  - an agent message that holds a handoff offer, in any state — including the collapsed "Chat
 *    started" line — and whatever else it carries (a token-limit warning, say). The bar rates and
 *    regenerates the whole response, which is the wrong thing to offer beside a prompt to act, so
 *    the whole bar goes rather than pretending part of it still applies.
 */
export function suppressesAnswerActions(message) {
    var _a, _b;
    if (SUPPORT_NO_ACTION_BAR_ROLES.includes(message.role))
        return true;
    return ((_b = (_a = message.envelope) === null || _a === void 0 ? void 0 : _a.blocks) !== null && _b !== void 0 ? _b : []).some((block) => SUPPORT_NO_ANSWER_ACTIONS_BLOCK_TYPES.includes(block.type));
}
